/** A teammate: their work in four numbers and the AI employees that report to them. */
import { Link } from "react-router-dom";
import { ArrowRight, Bot } from "lucide-react";
import { Avatar } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Card, firstName } from "@/features/command/ui";
import { AgentCard } from "./AgentCard";
import type { Agent, Person, TeamData, TeamMember, WorkTask } from "../types";

export function PersonCard({
  m, agents, team, people, wsTz, isMe, onChanged, onOpenTask, onToggleTask,
}: {
  m: TeamMember;
  agents: Agent[];
  team: TeamData;
  people: Person[];
  wsTz: string;
  isMe: boolean;
  onChanged: () => void;
  onOpenTask: (t: WorkTask) => void;
  onToggleTask: (t: WorkTask) => void;
}) {
  const first = firstName(m.name);
  const stats = [
    { label: "Open", value: m.open, to: `/work?person=${m.id}&view=today`, tone: "" },
    { label: "Late", value: m.overdue, to: `/work?person=${m.id}&view=today`, tone: m.overdue ? "text-rose-300" : "" },
    { label: "Done this week", value: m.done_week, to: null, tone: m.done_week ? "text-emerald-300" : "" },
    { label: "Routines", value: m.routines, to: `/work?person=${m.id}&view=routines`, tone: "" },
  ];
  return (
    <Card className="flex min-w-0 flex-col p-4">
      <div className="flex items-center gap-3">
        <Avatar name={m.name} email={m.email} src={m.image} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-base font-semibold text-zinc-50">{first || m.name}</span>
            {isMe && <span className="rounded-full bg-indigo-500/15 px-1.5 py-px text-[10px] font-semibold text-indigo-200 ring-1 ring-inset ring-indigo-400/25">you</span>}
          </div>
          <div className="truncate text-xs text-zinc-500">{m.email}</div>
        </div>
        <Link to={`/work?person=${m.id}`} className="inline-flex shrink-0 items-center gap-1 text-xs text-indigo-300 hover:underline">
          Their tasks <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-2">
        {stats.map((s) => {
          const body = (
            <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 px-2 py-2 text-center transition-colors hover:border-zinc-700">
              <div className={cn("text-xl font-semibold tabular-nums text-zinc-50", s.tone)}>{s.value}</div>
              <div className="mt-0.5 text-[10px] leading-tight text-zinc-500">{s.label}</div>
            </div>
          );
          return s.to ? <Link key={s.label} to={s.to}>{body}</Link> : <div key={s.label}>{body}</div>;
        })}
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          <Bot className="h-3.5 w-3.5 text-indigo-300" /> AI employees reporting to {first}
        </div>
        {agents.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-800 px-3 py-3 text-xs leading-relaxed text-zinc-500">
            {team.ai_available
              ? `None yet. Ask SyncSuite AI to make one for ${first}, then set it to report to ${first} here.`
              : "AI employees appear here once connected."}
          </div>
        ) : (
          <div className="min-w-0 space-y-3 border-l-2 border-indigo-400/20 pl-3">
            {agents.map((a) => (
              <AgentCard key={a.slug} agent={a} people={people} team={team} wsTz={wsTz} onChanged={onChanged} onOpenTask={onOpenTask} onToggleTask={onToggleTask} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
